import { useCallback } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { ADNode } from '@atlaskit/editor-common/validator';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useShowConvertToLivePageFlag } from '@confluence/editor-flags/entry-points/useShowConvertToLivePageFlag.ts';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

import { ConvertToLivePageDialog } from './LazyConvertToLivePageDialog.tsx';
import type { ADFOrigin } from './ConvertToLivePageDialog.tsx';
import { convertDraftToLivePage } from './convertDraftToLivePage.tsx';
import useSwitchToLivePageSubTypeFragment, {
	type useSwitchToLivePageSubTypeFragment$data,
} from './__generated__/useSwitchToLivePageSubTypeFragment.graphql.ts';

export type RetrieveEditorTitleAndContent = () => Promise<{
	title?: string | null;
	adf: ADNode;
	syncRev?: string;
	ncsStepVersion?: string;
}>;

type CommonProps = {
	contentId: string;
	spaceId: string | undefined;
	analyticsSource: string;
	onSuccessfulConversion?: () => void;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent | undefined;
	flags: FlagsStateContainer;
};

type PropsForUnpublishedDraft = {
	isUnpublishedDraft: true;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	saveDraft?: (arg?: any) => Promise<void>;
	// We don't use `adfOrigin` in this case
	adfOrigin?: any;
} & CommonProps;

type PropsForPublishedPage = {
	isUnpublishedDraft: false;
	adfOrigin: ADFOrigin;
	// We don't use `saveDraft` in this case
	saveDraft?: any;
} & CommonProps;
export type UseSwitchToLivePageProps = PropsForUnpublishedDraft | PropsForPublishedPage;

export type UseSwitchToLivePageReturnType = () => Promise<void>;

export const useSwitchToLivePage = (
	props: UseSwitchToLivePageProps,
): UseSwitchToLivePageReturnType => {
	const {
		contentId,
		spaceId,
		isUnpublishedDraft,
		analyticsSource,
		adfOrigin,
		onSuccessfulConversion,
		retrieveEditorTitleAndContent,
		flags,
		saveDraft,
	} = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();
	const showConvertToLivePageFlag = useShowConvertToLivePageFlag({ flags });

	const onSuccess = useCallback(() => {
		createAnalyticsEvent?.({
			type: 'sendUIEvent',
			data: {
				action: 'convertedToLivePage',
				actionSubject: 'page',
				source: analyticsSource,
				objectId: contentId,
				objectType: 'page',
				containerId: spaceId,
				containerType: 'space',
				attributes: {
					isDraft: isUnpublishedDraft,
				},
			},
		}).fire();

		onSuccessfulConversion?.();
		showConvertToLivePageFlag({ converterWasCurrentUser: true });
	}, [
		createAnalyticsEvent,
		analyticsSource,
		contentId,
		spaceId,
		isUnpublishedDraft,
		onSuccessfulConversion,
		showConvertToLivePageFlag,
	]);

	const writeSubTypeFragment = useWriteFragmentInterop<useSwitchToLivePageSubTypeFragment$data>({
		fragmentSpec: graphql`
			fragment useSwitchToLivePageSubTypeFragment on Content {
				subType
			}
		`,
		fragmentArtifact: useSwitchToLivePageSubTypeFragment,
	});

	return useCallback(async () => {
		createAnalyticsEvent?.({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'switchToLiveEdit',
				source: analyticsSource,
				objectId: contentId,
				objectType: 'page',
				containerId: spaceId,
				containerType: 'space',
				attributes: {
					isDraft: isUnpublishedDraft,
				},
			},
		}).fire();

		if (!isUnpublishedDraft) {
			showModal(ConvertToLivePageDialog, {
				contentId,
				onConfirm: onSuccess,
				adfOrigin,
				retrieveEditorTitleAndContent,
			});
		} else {
			// Trigger draft sync before converting. If we don't do this and the user recently updated their draft title then quickly converted the draft, draft sync may not have triggered and we may not identify their title conflict.
			await saveDraft?.();
			void convertDraftToLivePage({
				contentId,
				spaceId,
				onSuccess,
				flags,
				retrieveEditorTitleAndContent,
				writeSubTypeFragment,
			});
		}
	}, [
		createAnalyticsEvent,
		analyticsSource,
		contentId,
		spaceId,
		isUnpublishedDraft,
		showModal,
		onSuccess,
		adfOrigin,
		retrieveEditorTitleAndContent,
		saveDraft,
		flags,
		writeSubTypeFragment,
	]);
};
